import { toast } from 'react-toastify';
import {
    fetchBulkSchedulePreview,
    fetchCreatePreview,
    fetchDeletePreview,
    fetchReorderPreview,
    fetchSchedulePreview,
    taskPayloadHasScheduleInput,
    wouldAffectOtherTasks,
} from './taskSchedulingUtils';

export const SCHEDULE_RECALC_ERROR =
    'Schedule could not be recalculated. No schedule changes were applied.';

export const showScheduleError = (error, fallback = SCHEDULE_RECALC_ERROR) => {
    console.error(fallback, error);
    toast.error(fallback);
};

/**
 * Preview a schedule mutation; open confirmation when other tasks would change.
 */
export async function requestScheduleMutation({
    task,
    payload,
    fieldLabel,
    defaultUpdateOnly = true,
    previewFlags = {},
    setPending,
    applyPatch,
    setPreviewTaskId,
}) {
    try {
        setPreviewTaskId?.(task.id);
        const preview = await fetchSchedulePreview(task.id, payload, previewFlags);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                task,
                payload,
                fieldLabel,
                affectedCount: preview.affected_count,
                previewFlags,
            });
            return;
        }
        await applyPatch(task, payload, { updateOnly: defaultUpdateOnly, ...previewFlags });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export async function requestDeadlineMutation({
    task,
    deadline,
    setPendingDeadline,
    setPendingSchedule,
    applyPatch,
    setPreviewTaskId,
}) {
    const payload = { deadline: deadline || null };

    if (!deadline) {
        await requestScheduleMutation({
            task,
            payload,
            fieldLabel: 'Deadline',
            defaultUpdateOnly: true,
            setPending: setPendingSchedule,
            applyPatch,
            setPreviewTaskId,
        });
        return;
    }

    const hasDuration = task?.duration != null && task.duration !== '';
    try {
        setPreviewTaskId?.(task.id);
        const preview = await fetchSchedulePreview(task.id, payload);
        setPendingDeadline({
            task,
            payload,
            canAdjustTimeline: Boolean(hasDuration),
            affectedCount: preview.affected_count,
        });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export async function requestBulkDeadlineScheduleMutation({
    taskIds,
    deadline,
    setPending,
    applyBulkPatch,
    setPreviewTaskId,
}) {
    const mutation = { deadline: deadline || null };
    try {
        setPreviewTaskId?.('bulk-deadline');
        const preview = await fetchBulkSchedulePreview(taskIds, mutation);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                taskIds,
                payload: mutation,
                fieldLabel: 'Deadline',
                affectedCount: preview.affected_count,
                bulkDeadlineUpdate: true,
            });
            return;
        }
        await applyBulkPatch(taskIds, deadline, { updateOnly: true });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export const isCompletedStatus = status => status === 'COMPLETED' || status === 'COMPLETE';

/** Completing a key task also clears the star — one payload for every status patch path. */
export function statusPatchBody(task, newStatus) {
    const body = { status: newStatus };
    if (isCompletedStatus(newStatus) && task?.starred) {
        body.starred = false;
    }
    return body;
}

/** Status changes that may shift schedule anchors (Working on it / Completed). */
export function statusNeedsSchedulePreview(normalizedStatus, prevStatus) {
    const scheduleStatuses = new Set(['WORKING_ON_IT', 'COMPLETED', 'COMPLETE']);
    return scheduleStatuses.has(normalizedStatus) || scheduleStatuses.has(prevStatus);
}

export function shouldConfirmPauseCancelResume(normalized, prev) {
    if (['PAUSED', 'CANCELLED'].includes(normalized) && prev !== normalized) {
        return { action: normalized === 'CANCELLED' ? 'cancel' : 'pause' };
    }
    if (['PAUSED', 'CANCELLED'].includes(prev) && !['PAUSED', 'CANCELLED'].includes(normalized)) {
        return { action: 'resume' };
    }
    return null;
}

export async function requestStatusScheduleMutation({
    task,
    newStatus,
    setPending,
    applyPatch,
    setPreviewTaskId,
}) {
    await requestScheduleMutation({
        task,
        payload: statusPatchBody(task, newStatus),
        fieldLabel: 'Status',
        defaultUpdateOnly: true,
        setPending,
        applyPatch,
        setPreviewTaskId,
    });
}

export async function requestReorderScheduleMutation({
    taskOrders,
    setPending,
    applyReorder,
    setPreviewTaskId,
}) {
    try {
        setPreviewTaskId?.('reorder');
        const preview = await fetchReorderPreview(taskOrders);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                taskOrders,
                fieldLabel: 'Task order',
                affectedCount: preview.affected_count,
            });
            return;
        }
        await applyReorder(taskOrders, { recalculateDownstream: false });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export async function requestCreateScheduleMutation({
    payload,
    setPending,
    applyCreate,
    setPreviewTaskId,
}) {
    if (!taskPayloadHasScheduleInput(payload)) {
        await applyCreate(payload, { recalculateDownstream: false });
        return;
    }
    try {
        setPreviewTaskId?.('create');
        const preview = await fetchCreatePreview(payload);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                payload,
                fieldLabel: 'Insert',
                affectedCount: preview.affected_count,
                createTask: true,
            });
            return;
        }
        await applyCreate(payload, { recalculateDownstream: false });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export async function requestBulkStatusScheduleMutation({
    taskIds,
    newStatus,
    setPending,
    applyBulkPatch,
    setPreviewTaskId,
}) {
    const mutation = { status: newStatus };
    try {
        setPreviewTaskId?.('bulk');
        const preview = await fetchBulkSchedulePreview(taskIds, mutation);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                taskIds,
                payload: mutation,
                fieldLabel: 'Status',
                affectedCount: preview.affected_count,
                bulkUpdate: true,
            });
            return;
        }
        await applyBulkPatch(taskIds, newStatus, { updateOnly: true });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}

export async function requestDeleteScheduleMutation({
    task,
    setPending,
    applyDelete,
    setPreviewTaskId,
}) {
    try {
        setPreviewTaskId?.(task.id);
        const preview = await fetchDeletePreview(task.id);
        if (wouldAffectOtherTasks(preview)) {
            setPending({
                task,
                fieldLabel: 'Delete',
                affectedCount: preview.affected_count,
            });
            return;
        }
        await applyDelete(task, { recalculateDownstream: false });
    } catch (error) {
        showScheduleError(error);
        throw error;
    } finally {
        setPreviewTaskId?.(null);
    }
}
